// Analytics Overview — the original single-screen analytics snapshot:
// 4-stat strip header, outcome breakdown, concern mix, daily volume,
// top entry points, top agents. Pure-client aggregation.

import React, { useMemo } from 'react';
import {
  Badge,
  Grid,
  Group,
  Paper,
  Progress,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';

import { scoreCall } from '../concernScore';

export function AnalyticsOverview({ sessions }) {
  const stats = useMemo(() => computeStats(sessions ?? []), [sessions]);

  return (
    <Stack>
      <StatStrip stats={stats} />
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 6 }}>
          <OutcomeBreakdownCard stats={stats} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <ConcernMixCard stats={stats} />
        </Grid.Col>
        <Grid.Col span={12}>
          <DailyVolumeCard stats={stats} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TopListCard title="Top entry points" items={stats.topEntryPoints} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <TopListCard title="Top agents" items={stats.topAgents} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function StatStrip({ stats }) {
  const items = [
    { label: 'Total sessions', value: stats.total },
    { label: 'Handled', value: `${stats.handled} (${pct(stats.handled, stats.total)}%)` },
    { label: 'Escalated', value: `${stats.escalated} (${pct(stats.escalated, stats.total)}%)` },
    { label: 'Abandoned / Failed', value: `${stats.abandoned + stats.failed} (${pct(stats.abandoned + stats.failed, stats.total)}%)` },
  ];
  return (
    <Grid gutter="xs">
      {items.map((it) => (
        <Grid.Col key={it.label} span={{ base: 6, md: 3 }}>
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" c="dimmed">{it.label}</Text>
            <Text size="xl" fw={700}>{it.value}</Text>
          </Paper>
        </Grid.Col>
      ))}
    </Grid>
  );
}

function OutcomeBreakdownCard({ stats }) {
  const segments = [
    { value: pct(stats.handled, stats.total), color: 'teal', label: `Handled (${stats.handled})` },
    { value: pct(stats.escalated, stats.total), color: 'blue', label: `Escalated (${stats.escalated})` },
    { value: pct(stats.abandoned, stats.total), color: 'orange', label: `Abandoned (${stats.abandoned})` },
    { value: pct(stats.failed, stats.total), color: 'red', label: `Failed (${stats.failed})` },
    { value: pct(stats.unknown, stats.total), color: 'gray', label: `Unknown (${stats.unknown})` },
  ].filter((seg) => seg.value > 0);

  return (
    <Paper withBorder p="md" radius="md" style={{ height: '100%' }}>
      <Title order={5} mb="xs">Outcome breakdown</Title>
      {segments.length === 0 ? (
        <Text size="sm" c="dimmed">No sessions in window.</Text>
      ) : (
        <>
          <Progress.Root size="xl">
            {segments.map((seg) => (
              <Progress.Section key={seg.label} value={seg.value} color={seg.color}>
                <Progress.Label>{seg.value > 8 ? `${seg.value}%` : ''}</Progress.Label>
              </Progress.Section>
            ))}
          </Progress.Root>
          <Stack gap={4} mt="sm">
            {segments.map((seg) => (
              <Group key={seg.label} gap="xs">
                <Badge size="xs" color={seg.color} variant="light">{seg.value}%</Badge>
                <Text size="xs">{seg.label}</Text>
              </Group>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
}

function ConcernMixCard({ stats }) {
  const buckets = stats.concernBuckets;
  const total = stats.total || 1;
  const segments = [
    { label: 'None (0–14)', value: pct(buckets.none, total), color: 'gray', count: buckets.none },
    { label: 'Low (15–34)', value: pct(buckets.low, total), color: 'yellow', count: buckets.low },
    { label: 'Medium (35–59)', value: pct(buckets.medium, total), color: 'orange', count: buckets.medium },
    { label: 'High (60+)', value: pct(buckets.high, total), color: 'red', count: buckets.high },
  ].filter((seg) => seg.value > 0);

  return (
    <Paper withBorder p="md" radius="md" style={{ height: '100%' }}>
      <Title order={5} mb="xs">Concern mix</Title>
      <Text size="xs" c="dimmed" mb="xs">
        Count of sessions by concern score (see Call Search table for the detail).
      </Text>
      {segments.length === 0 ? (
        <Text size="sm" c="dimmed">No sessions in window.</Text>
      ) : (
        <>
          <Progress.Root size="xl">
            {segments.map((seg) => (
              <Progress.Section key={seg.label} value={seg.value} color={seg.color}>
                <Progress.Label>{seg.value > 8 ? `${seg.value}%` : ''}</Progress.Label>
              </Progress.Section>
            ))}
          </Progress.Root>
          <Stack gap={4} mt="sm">
            {segments.map((seg) => (
              <Group key={seg.label} gap="xs">
                <Badge size="xs" color={seg.color} variant="light">{seg.count}</Badge>
                <Text size="xs">{seg.label}</Text>
              </Group>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
}

function DailyVolumeCard({ stats }) {
  const days = stats.byDay;
  if (days.length === 0) {
    return (
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="xs">Daily volume</Title>
        <Text size="sm" c="dimmed">No sessions in window.</Text>
      </Paper>
    );
  }
  const max = Math.max(...days.map((d) => d.total)) || 1;
  const barWidth = 36;
  const gap = 8;
  const height = 160;
  const chartHeight = 140;
  const svgWidth = days.length * (barWidth + gap);

  return (
    <Paper withBorder p="md" radius="md">
      <Title order={5} mb="xs">Daily volume</Title>
      <Text size="xs" c="dimmed" mb="xs">
        Session count per day. Stacked by outcome.
      </Text>
      <div style={{ overflowX: 'auto' }}>
        <svg width={Math.max(svgWidth, 320)} height={height} style={{ display: 'block' }}>
          {days.map((d, i) => {
            let yCursor = chartHeight;
            const stacks = [
              { count: d.handled, color: '#12b886' },
              { count: d.escalated, color: '#228be6' },
              { count: d.abandoned, color: '#fd7e14' },
              { count: d.failed, color: '#fa5252' },
              { count: d.unknown, color: '#adb5bd' },
            ];
            const x = i * (barWidth + gap);
            return (
              <g key={d.day}>
                {stacks.map((s, j) => {
                  if (s.count === 0) return null;
                  const h = Math.round((s.count / max) * chartHeight);
                  yCursor -= h;
                  return <rect key={j} x={x} y={yCursor} width={barWidth} height={h} fill={s.color} />;
                })}
                <text x={x + barWidth / 2} y={chartHeight - 4} textAnchor="middle" fontSize="11" fill="#495057">
                  {d.total}
                </text>
                <text x={x + barWidth / 2} y={height - 4} textAnchor="middle" fontSize="10" fill="#868e96">
                  {shortDate(d.day)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <Group gap="md" mt="xs">
        <LegendDot color="#12b886" label="Handled" />
        <LegendDot color="#228be6" label="Escalated" />
        <LegendDot color="#fd7e14" label="Abandoned" />
        <LegendDot color="#fa5252" label="Failed" />
        <LegendDot color="#adb5bd" label="Unknown" />
      </Group>
    </Paper>
  );
}

function LegendDot({ color, label }) {
  return (
    <Group gap={4}>
      <span style={{ display: 'inline-block', width: 10, height: 10, background: color, borderRadius: 2 }} />
      <Text size="xs" c="dimmed">{label}</Text>
    </Group>
  );
}

function TopListCard({ title, items }) {
  if (!items || items.length === 0) {
    return (
      <Paper withBorder p="md" radius="md" style={{ height: '100%' }}>
        <Title order={5} mb="xs">{title}</Title>
        <Text size="sm" c="dimmed">No data.</Text>
      </Paper>
    );
  }
  const max = Math.max(...items.map((it) => it.total)) || 1;
  return (
    <Paper withBorder p="md" radius="md" style={{ height: '100%' }}>
      <Title order={5} mb="xs">{title}</Title>
      <Table verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '40%' }}>Name</Table.Th>
            <Table.Th>Volume</Table.Th>
            <Table.Th style={{ width: 110 }}>Escalation / Abandon</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((it) => {
            const esc = pct(it.escalated, it.total);
            const ab = pct(it.abandoned + it.failed, it.total);
            return (
              <Table.Tr key={it.name}>
                <Table.Td>
                  <Text size="sm" fw={500}>{it.name || <em>unknown</em>}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Progress value={(it.total / max) * 100} size="sm" style={{ flex: 1 }} />
                    <Text size="xs" c="dimmed">{it.total}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Badge size="xs" color="blue" variant="light">esc {esc}%</Badge>
                    <Badge size="xs" color="orange" variant="light">ab {ab}%</Badge>
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}

// -- Pure stat computation ----------------------------------------------
// Operates on raw contactAIAgentSession rows (same shape returned by
// useReportWindow). Shim for the fields that live under different names
// on the normalized CallRow shape.

function computeStats(sessions) {
  const stats = {
    total: sessions.length,
    handled: 0,
    escalated: 0,
    abandoned: 0,
    failed: 0,
    unknown: 0,
    concernBuckets: { none: 0, low: 0, medium: 0, high: 0 },
    byDay: [],
    topEntryPoints: [],
    topAgents: [],
  };

  const dayMap = new Map();
  const entryMap = new Map();
  const agentMap = new Map();

  for (const s of sessions) {
    const outcome = s.interactionOutcome;
    const bucket = outcomeBucket(outcome);
    stats[bucket] += 1;

    const { score } = scoreCall(toScoringShape(s));
    if (score >= 60) stats.concernBuckets.high += 1;
    else if (score >= 35) stats.concernBuckets.medium += 1;
    else if (score >= 15) stats.concernBuckets.low += 1;
    else stats.concernBuckets.none += 1;

    const day = isoDay(s.sessionStartTime);
    bumpDay(dayMap, day, bucket);
    bumpTop(entryMap, s.entryPoint?.name || null, bucket);
    bumpTop(agentMap, s.agentName || null, bucket);
  }

  stats.byDay = [...dayMap.values()].sort((a, b) => a.day.localeCompare(b.day));
  stats.topEntryPoints = topN(entryMap, 5);
  stats.topAgents = topN(agentMap, 5);
  return stats;
}

function toScoringShape(s) {
  return {
    outcome: s.interactionOutcome,
    overallEvalScore: s.overallEvalScore,
    evalInteractionsFailureCount: s.evalInteractionsFailureCount,
    deadAirCount: s.deadAirCount,
    bargeInCount: s.bargeInCount,
    avgResponseMs: s.averageResponseTime,
    messageCount: s.noOfTotalMessages,
  };
}

function outcomeBucket(outcome) {
  if (outcome === 'HANDLED' || outcome === 'COMPLETED') return 'handled';
  if (outcome === 'ESCALATED') return 'escalated';
  if (outcome === 'ABANDONED') return 'abandoned';
  if (outcome === 'FAILED') return 'failed';
  return 'unknown';
}
function bumpDay(map, day, bucket) {
  if (!day) return;
  if (!map.has(day)) map.set(day, { day, total: 0, handled: 0, escalated: 0, abandoned: 0, failed: 0, unknown: 0 });
  const entry = map.get(day);
  entry.total += 1;
  entry[bucket] += 1;
}
function bumpTop(map, name, bucket) {
  if (!name) return;
  if (!map.has(name)) map.set(name, { name, total: 0, handled: 0, escalated: 0, abandoned: 0, failed: 0, unknown: 0 });
  const entry = map.get(name);
  entry.total += 1;
  entry[bucket] += 1;
}
function topN(map, n) {
  return [...map.values()].sort((a, b) => b.total - a.total).slice(0, n);
}
function isoDay(ts) {
  if (ts == null) return null;
  const n = typeof ts === 'number' ? ts : Number(ts);
  const d = !isNaN(n) && n > 1e12 ? new Date(n) : new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}
function shortDate(iso) {
  if (!iso || iso.length < 10) return iso ?? '';
  return `${iso.slice(5, 7)}/${iso.slice(8, 10)}`;
}
function pct(count, total) {
  if (!total) return 0;
  return Math.round((count / total) * 100);
}
